/** Integrations route (IA §2 Manage › Admin › Integrations). Tabs, filters and selection live in the URL. */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';
import type { DeliveryStatus } from './types';

const oneOf = <T extends string>(v: unknown, values: readonly T[]): T | undefined => (values.includes(v as T) ? (v as T) : undefined);

export const DELIVERY_STATUSES = ['pending', 'retrying', 'delivered', 'failed', 'cancelled'] as const satisfies readonly DeliveryStatus[];
export const WEBHOOK_SORTS = ['name', '-name', 'created_at', '-created_at'] as const;

export interface IntegrationsSearch {
  /** webhooks (default) | deliveries */
  tab?: 'deliveries';
  q?: string;
  status?: 'active' | 'disabled';
  sort?: (typeof WEBHOOK_SORTS)[number];
  /** the selected webhook (detail pane or sheet) */
  webhook?: string;
  /** delivery log filters */
  dstatus?: DeliveryStatus;
  dwebhook?: string;
  /** the open delivery (dialog) */
  delivery?: string;
  /** dialogs: new webhook, edit one */
  create?: '1';
  edit?: string;
}

export const integrationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/integrations',
  staticData: { permission: 'integrations.webhook.read' },
  validateSearch: (s: Record<string, unknown>): IntegrationsSearch => ({
    tab: oneOf(s.tab, ['deliveries'] as const),
    q: str(s.q),
    status: oneOf(s.status, ['active', 'disabled'] as const),
    sort: oneOf(s.sort, WEBHOOK_SORTS),
    webhook: str(s.webhook),
    dstatus: oneOf(s.dstatus, DELIVERY_STATUSES),
    dwebhook: str(s.dwebhook),
    delivery: str(s.delivery),
    create: s.create === '1' || s.create === 1 ? '1' : undefined,
    edit: str(s.edit),
  }),
  component: lazyRouteComponent(() => import('./IntegrationsPage')),
});
